import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Copy, Check, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  usePostAgents,
  getGetAgentsQueryKey,
} from '@/api/control/control-plane/control-plane'
import type { CreateAgentResponse } from '@/api/control/missionImpactAPI.schemas'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface CreateAgentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
}

/**
 * Dialog for creating a new self-hosted agent.
 * Shows a form to enter the agent name, then displays the one-time registration token.
 */
export function CreateAgentDialog({ open, onOpenChange, orgId }: CreateAgentDialogProps) {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const { handleApiError } = useError()
  const [name, setName] = useState('')
  const [result, setResult] = useState<CreateAgentResponse | null>(null)
  const [tokenCopied, setTokenCopied] = useState(false)

  const { mutate: createAgent, isPending } = usePostAgents({
    mutation: {
      onSuccess: (response) => {
        setResult(response.data)
        void queryClient.invalidateQueries({
          queryKey: getGetAgentsQueryKey(orgId),
        })
      },
      onError: (error) => {
        handleApiError(error, { context: 'CreateAgentDialog.createAgent' })
      },
    },
  })

  const handleCreate = () => {
    createAgent({ orgId, data: { name } })
  }

  const handleCopyToken = async () => {
    if (!result?.registrationToken) return
    try {
      await navigator.clipboard.writeText(result.registrationToken)
      setTokenCopied(true)
      toast.success(t.agents.createDialog.tokenCopied)
      setTimeout(() => { setTokenCopied(false); }, 2000)
    } catch {
      // Clipboard API may fail in some contexts
    }
  }

  const handleClose = () => {
    setName('')
    setResult(null)
    setTokenCopied(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t.agents.createDialog.successTitle}</DialogTitle>
              <DialogDescription>
                {t.agents.createDialog.successDescription}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t.agents.createDialog.registrationToken}</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={result.registrationToken ?? ''}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => { void handleCopyToken(); }}
                  >
                    {tokenCopied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {result.tokenExpiresAtUtc && (
                <p className="text-sm text-muted-foreground">
                  {t.agents.createDialog.tokenExpires}: {new Date(result.tokenExpiresAtUtc).toLocaleString()}
                </p>
              )}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t.agents.createDialog.tokenWarning}
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>
                {t.agents.createDialog.close}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t.agents.createDialog.title}</DialogTitle>
              <DialogDescription>
                {t.agents.createDialog.description}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="agent-name">{t.agents.createDialog.nameLabel}</Label>
                <Input
                  id="agent-name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); }}
                  placeholder={t.agents.createDialog.namePlaceholder}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                {t.agents.createDialog.cancel}
              </Button>
              <LoadingButton
                type="button"
                onClick={handleCreate}
                disabled={!name.trim()}
                isLoading={isPending}
              >
                {t.agents.createDialog.create}
              </LoadingButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
